import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, getDocFromServer } from 'firebase/firestore';

/**
 * Route guard for ALL /admin/* pages that require full setup.
 * Uses getDocFromServer to avoid stale-cache redirects.
 * For fully-completed users, bypasses entirely via local cache to avoid loading UI.
 */
function ProtectedRoute({ children }) {
  const location = useLocation();
  const [status, setStatus] = useState('pending'); // 'pending' | 'checking' | 'ready' | 'redirect'
  const [redirectTo, setRedirectTo] = useState(null);

  useEffect(() => {
    const auth = getAuth();

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setRedirectTo('/admin/login?reason=signed-out');
        setStatus('redirect');
        return;
      }

      const db = getFirestore();

      // 1. Instant bypass: read local cache first (fast, zero network)
      try {
        const cacheSnap = await getDoc(doc(db, 'users', user.uid));
        if (cacheSnap.exists()) {
          const data = cacheSnap.data();
          const otpVerified = data?.signupOtpVerified;
          const otpRequired = data?.signupOtpRequired === true || otpVerified === false;
          if (otpRequired && otpVerified !== true) {
            setRedirectTo(`/admin/signup/otp${location.search || ''}`);
            setStatus('redirect');
            return;
          }

          const sg = data?.setupGuide;
          const hasPlan = data?.activeSubscriptionStatus === 'active';
          if (sg?.allRequiredDone === true && hasPlan) {
            console.log('[ProtectedRoute] Cache bypass — fully completed user');
            setRedirectTo(null);
            setStatus('ready');
            return;
          }
        }
      } catch (e) {
        // Ignore cache read errors, fall through to server check
      }

      // 2. Server check (for incomplete users or no cache)
      setStatus('checking');
      try {
        const docSnap = await getDocFromServer(doc(db, 'users', user.uid));

        if (!docSnap.exists()) {
          setRedirectTo('/admin/login?reason=no-account-doc');
          setStatus('redirect');
          return;
        }

        const data = docSnap.data();
        const otpVerified = data?.signupOtpVerified;
        const otpRequired = data?.signupOtpRequired === true || otpVerified === false;
        if (otpRequired && otpVerified !== true) {
          setRedirectTo(`/admin/signup/otp${location.search || ''}`);
          setStatus('redirect');
          return;
        }

        const stripeAccountId = data?.stripeAccountId;
        const onboardingComplete =
          data?.stripeOnboardingComplete === true ||
          (data?.stripeChargesEnabled === true &&
            data?.stripePayoutsEnabled === true &&
            data?.stripeDetailsSubmitted === true);
        const hasPlan = data?.activeSubscriptionStatus === 'active';

        console.log('[ProtectedRoute] Server status:', {
          stripeAccountId: !!stripeAccountId,
          onboardingComplete,
          hasPlan,
        });

        if (!stripeAccountId || !onboardingComplete) {
          setRedirectTo('/admin/onboarding');
          setStatus('redirect');
        } else if (!hasPlan) {
          setRedirectTo('/admin/plans');
          setStatus('redirect');
        } else {
          setRedirectTo(null);
          setStatus('ready');
        }
      } catch (error) {
        console.error('[ProtectedRoute] Server read error:', error);
        // On error, be permissive — don’t block the user
        setRedirectTo(null);
        setStatus('ready');
      }
    });

    return () => unsubAuth();
  }, [location.search]);

  // For fully-completed users: render children immediately (zero loading UI)
  if (status === 'ready') {
    return children;
  }

  // While auth is being determined: show nothing (no spinner flash)
  if (status === 'pending') {
    return <div style={{ display: 'none' }} />;
  }

  // Only show spinner for users who need server verification
  if (status === 'checking') {
    return (
      <div className="stripe-page loading">
        <div className="stripe-card">
          <div className="loading-spinner"></div>
          <h1>Checking your account...</h1>
          <p>Please wait while we verify your setup.</p>
        </div>
      </div>
    );
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

export default ProtectedRoute;
